import LoginForm from "@/components/auth/LoginForm";
import AuthTransition from "@/components/auth/AuthTransition";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, Zap, Shield, CheckCircle, BarChart3, Users } from "lucide-react";

const Login = () => {
  const { isAuthenticated } = useAuth();

  // If user is already authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthTransition>
      <div className="min-h-[calc(100vh-env(safe-area-inset-bottom,0px))] bg-slate-50 dark:bg-slate-900" style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgb(148_163_184_/_0.15)_1px,_transparent_0)] [background-size:24px_24px]" />
        
        <div className="relative flex pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)]" style={{ minHeight: 'calc(var(--vh, 1vh) * 100)', paddingBottom: 'calc(var(--vh, 1vh) * 8)' }}>
          {/* Left Side - Branding */}
          <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 xl:px-20 bg-white dark:bg-slate-800 relative">
            {/* Subtle decorative elements */}
            <div className="absolute top-20 right-20 w-32 h-32 bg-blue-50 dark:bg-blue-900/20 rounded-full opacity-60" />
            <div className="absolute bottom-20 left-20 w-24 h-24 bg-indigo-50 dark:bg-indigo-900/20 rounded-full opacity-40" />
            <div className="absolute top-1/2 right-10 w-16 h-16 bg-purple-50 dark:bg-purple-900/20 rounded-full opacity-30" />
            
            <div className="max-w-md relative z-10">
              {/* Logo */}
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  ManterFlow
                </h1>
              </div>

              {/* Hero Text */}
              <h2 className="text-4xl xl:text-5xl font-bold text-slate-900 dark:text-white leading-tight mb-6">
                Modern Collection
                <span className="text-blue-600 dark:text-blue-400 block">
                  Management
                </span>
              </h2>
              <p className="text-slate-600 dark:text-slate-300 text-lg mb-8 leading-relaxed">
                Streamline your fee collection process with our intelligent platform. 
                Track, manage, and optimize collections with ease.
              </p>

              {/* Features */}
              <div className="space-y-4">
                <div className="flex items-center text-slate-700 dark:text-slate-300">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
                    <Shield className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                  <span>Enterprise-grade security</span>
                </div>
                <div className="flex items-center text-slate-700 dark:text-slate-300">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mr-3">
                    <BarChart3 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span>Advanced analytics & insights</span>
                </div>
                <div className="flex items-center text-slate-700 dark:text-slate-300">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span>Multi-user collaboration</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="flex-1 flex flex-col justify-center px-6 py-6 lg:py-12 lg:px-8 bg-slate-50 dark:bg-slate-900 overflow-y-auto md:overflow-visible max-h-[90vh] md:max-h-none" style={{ maxHeight: 'calc(var(--vh, 1vh) * 90)' }}>
            <div className="mx-auto w-full max-w-md">
              {/* Mobile Logo */}
              <div className="lg:hidden text-center mb-8">
                <div className="inline-flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                    ManterFlow
                  </h1>
                </div>
                <p className="text-slate-600 dark:text-slate-300 mt-2">
                  Welcome back! Please sign in to continue.
                </p>
              </div>

              {/* Desktop Welcome */}
              <div className="hidden lg:block text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  Welcome back
                </h2>
                <p className="text-slate-600 dark:text-slate-300">
                  Sign in to your account to continue
                </p>
              </div>

              {/* Login Form */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-xl border border-slate-200 dark:border-slate-700">
                <LoginForm />
              </div>

              {/* Sign Up Link */}
              <div className="mt-6 mb-4 text-center">
                <div className="text-center text-sm text-slate-600 dark:text-slate-400">
                  Don't have an account?{' '}
                  <Link 
                    to="/signup" 
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors font-medium"
                  >
                    Create Account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthTransition>
  );
};

export default Login;
